/**
 * App-wide preferences (theme, language, accent) under AppLocalData/app-settings/.
 * Browser dev: localStorage key apiboo-app-preferences.
 */
import { prefersFilesystemStorage } from './localWorkspaceStore';
import { clampTerminalFontSize, normalizeTerminalTheme } from '../utils/terminalTheme';
import { normalizeCloseOnExit } from '../utils/terminalCloseOnExit';
import { clampNotifySeconds } from '../components/terminal/commandNotify';
import { clampLongMessageKb } from '../utils/chatComposeLimits';
import { DEFAULT_ACCENT, normalizeAccentHex } from '../utils/accentColor';
import { DB_IDLE_DISCONNECT_DEFAULT, normalizeDbIdleDisconnect } from '../extensions/dbIdleDisconnect';
import { normalizeDbTypeColors } from '../components/dbManager/dbTypeColors';

const PREFS_DIR = 'app-settings';
const PREFS_FILE = 'app-settings/preferences.json';
const BROWSER_KEY = 'apiboo-app-preferences';
const LEGACY_THEME_KEY = 'api-client-theme';

export const DEFAULT_PREFERENCES = {
  version: 1,
  theme: 'system',
  language: 'en',
  accentColor: DEFAULT_ACCENT,
  // Legacy name for what is now `skippedUpdateVersion`. Still written (mirrored)
  // so a user who rolls back to 1.0.1 keeps their skip instead of getting the
  // old every-launch prompt back.
  dismissedUpdateVersion: null,
  // The exact version the user pressed "Skip this version" on. Permanent for
  // that version, and never revived by a newer one — see updatePromptPolicy.js.
  skippedUpdateVersion: null,
  // UTC day stamp ("YYYY-MM-DD") the update modal last AUTO-ANNOUNCED itself
  // on. The daily cap. Written at announcement, never by a dismissal handler.
  updatePromptLastShownDay: null,
  workspaceTransition: true,
  // General
  timeFormat: '24h',
  restoreLastSession: true,
  checkUpdatesFrequency: 'launch',
  // "Send usage data" (owner, 16.10.2026): OFF unless the user said yes. It is
  // asked once, right after sign-in (components/UsageConsentCard.js);
  // `usageDataAsked` records that the question was answered, either way.
  // Nothing feature-related leaves the machine until both are true.
  sendUsageData: false,
  usageDataAsked: false,
  // Chat link previews are opt-in: enabling them lets the app fetch pages that
  // others link, which reveals your IP to those sites. Off unless consented.
  linkPreviews: false,
  // Send read receipts (server-enforced, reciprocal). On by default.
  readReceipts: true,
  // Chat composer size handling. The server's cap is in BYTES (UTF-8), so a
  // non-Latin message reaches it far sooner than its character count suggests
  // — see utils/chatComposeLimits.js. On by default because the alternative is
  // a refused send with no way to recover the text.
  chatLongMessageAsFile: true,
  chatLongMessageKb: 12,
  chatShowSizeIndicator: true,
  // AI assistant — may it read HTTP response bodies? Opt-in, default OFF
  // (owner decision 13.09.2026; services/ai/responseAccess.js).
  aiReadResponses: false,
  // Notifications
  desktopNotifications: true, // OS toast for incoming chat messages (app unfocused)
  notificationSound: true,    // play new-message.mp3 on incoming messages
  closeToTray: true,          // closing the window keeps the app in the system tray
  // Open at sign-in: the default (on) was applied once on this device (services/launchAtLogin.js).
  launchAtLoginApplied: false,
  // Appearance
  density: 'balanced',
  sidebarPosition: 'left',
  uiFontSize: 12,
  showNavTooltips: true,
  accentFg: 'auto',
  // Terminal extension
  terminalFontSize: 11,
  // Close a pane when its program exits: 'clean' (code 0) | 'always' | 'never'.
  terminalCloseOnExit: 'clean',
  // The terminal follows the app theme by default; or always dark / light (05.10.2026).
  terminalTheme: 'follow',
  // macOS: Option sends Meta in the terminal. Off, as in Terminal.app (21.10.2026).
  terminalOptionAsMeta: false,
  // Windows / Linux: Ctrl+K inside a terminal goes to the shell (readline kill-line)
  // instead of opening the command palette. Off: the palette wins (palette PLAN §2.10).
  terminalSendCtrlK: false,
  // Notify when a command ran this long and the window / pane was not in view (card 5).
  terminalNotifyLongCommands: true,
  terminalNotifySeconds: 15,
  // FTP/SFTP — show dotfiles in both panes. ON by default: these panes exist to
  // administer servers, where `.env`, `.htaccess` and `.ssh` are usually the
  // POINT of connecting, not clutter to be filtered out.
  ftpShowHiddenFiles: true,
  // Mail catcher (local SMTP inbox). OFF at start by default (D2): a client
  // grabbing 1025 on every launch collides with a user's own Mailpit/MailHog.
  mailCatcherPort: 1025,
  mailCatcherAutoStart: false,
  mailCatcherKeep: 500,
  // DB Manager: close open connections after this long in the background
  // ('15m' | '30m' | '1h' | '2h' | 'never') — see extensions/dbIdleDisconnect.js.
  dbIdleDisconnect: DB_IDLE_DISCONNECT_DEFAULT,
  // Colour grid values by column type — { enabled, light: {cat: #hex}, dark }
  // (overrides only; see components/dbManager/dbTypeColors.js). On by default.
  dbTypeColors: { enabled: true, light: {}, dark: {} },
  // Editor
  editorFontFamily: 'JetBrains Mono',
  editorFontSize: 12,
  editorTabSize: 4,
  wordWrap: true,
  stickyScroll: true,
  defaultResponseView: 'pretty',
  // Max response size in MB, 0 = no limit (see utils/requestNetworkOptions).
  responseSizeLimitMb: 0,
  // Requests & Network
  requestTimeout: 30000,
  followRedirects: true,
  // No `sslVerification` here (24.09.2026). It was saved and never read, and
  // contradicted Settings → Certificates, whose `verifySSL` (certificateStore,
  // per account) is what the sender has always obeyed and is now the only SSL
  // switch. A stored `sslVerification` is dropped by the whitelist below and is
  // deliberately NOT migrated: copying a never-honoured `false` into the real
  // switch would turn verification off for someone who never saw it take effect.
  httpVersion: 'auto',
  useSystemProxy: false,
  proxyUrl: '',
  // Keyboard — overrides map: { [commandId]: keysSpec | null }. Empty = all defaults.
  keyboardShortcuts: {},
};

export const MAIL_CATCHER_PORT_DEFAULT = 1025;
export const MAIL_CATCHER_KEEP_MIN = 50;
export const MAIL_CATCHER_KEEP_MAX = 5000;
export const MAIL_CATCHER_KEEP_DEFAULT = 500;

/** Integer TCP port, 1–65535; anything else falls back to the default. */
export function clampMailCatcherPort(raw) {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1 || n > 65535) return MAIL_CATCHER_PORT_DEFAULT;
  return n;
}

/** Retention count, clamped to 50–5000 (the Rust store clamps the same way). */
export function clampMailCatcherKeep(raw) {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return MAIL_CATCHER_KEEP_DEFAULT;
  return Math.min(MAIL_CATCHER_KEEP_MAX, Math.max(MAIL_CATCHER_KEEP_MIN, n));
}

/**
 * Validate a keyboard-shortcuts overrides map. Each value must be a keys spec
 * ({ key, mod?, ctrl?, meta?, shift?, alt? }) or null (explicitly unbound).
 * @param {unknown} raw
 * @returns {Record<string, object|null>}
 */
export function normalizeKeyboardShortcuts(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [id, value] of Object.entries(raw)) {
    if (value === null) {
      out[id] = null;
      continue;
    }
    if (!value || typeof value !== 'object' || typeof value.key !== 'string' || !value.key) {
      continue;
    }
    const spec = { key: value.key };
    if (value.mod) spec.mod = true;
    if (value.ctrl) spec.ctrl = true;
    if (value.meta) spec.meta = true;
    if (value.shift) spec.shift = true;
    if (value.alt) spec.alt = true;
    out[id] = spec;
  }
  return out;
}

let saveTimer;

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

async function ensurePrefsDirFs() {
  const fs = await fsModule();
  await fs.mkdir(PREFS_DIR, {
    baseDir: fs.BaseDirectory.AppLocalData,
    recursive: true,
  });
}

function readBrowserPrefs() {
  try {
    const raw = localStorage.getItem(BROWSER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readLegacyTheme() {
  try {
    const raw = localStorage.getItem(LEGACY_THEME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const theme = parsed?.state?.theme ?? parsed?.theme;
    if (theme === 'dark' || theme === 'light') return theme;
  } catch {
    // ignore
  }
  return null;
}

function clearLegacyThemeStorage() {
  try {
    localStorage.removeItem(LEGACY_THEME_KEY);
  } catch {
    // ignore
  }
}

/**
 * "Send usage data" as the user actually chose it (owner, 16.10.2026).
 *
 * Until this version the toggle defaulted ON and a stored `true` was simply
 * that default: nobody chose it. So:
 *   - a file that already knows `usageDataAsked` (written by this version or
 *     later) is taken at its word, and sharing needs BOTH flags;
 *   - an older file: a stored `false` can only be the user switching it off
 *     (the default was on), so it stays off and is not asked again; anything
 *     else is unanswered — off, and asked once.
 */
export function normalizeUsageConsent(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const usageDataAsked = Object.prototype.hasOwnProperty.call(src, 'usageDataAsked')
    ? src.usageDataAsked === true
    : src.sendUsageData === false;
  return { usageDataAsked, sendUsageData: usageDataAsked && src.sendUsageData === true };
}

/**
 * @param {unknown} raw
 * @returns {typeof DEFAULT_PREFERENCES}
 */
export function normalizePreferences(raw) {
  const base = { ...DEFAULT_PREFERENCES };
  if (!raw || typeof raw !== 'object') return base;

  const validThemes = ['light', 'dark', 'system'];
  const theme = validThemes.includes(raw.theme) ? raw.theme : 'system';
  const language = raw.language === 'de' ? 'de' : 'en';
  const accentColor = normalizeAccentHex(raw.accentColor) ?? DEFAULT_ACCENT;
  const trimmedOrNull = (value) =>
    typeof value === 'string' && value.trim() ? value.trim() : null;
  // A legacy `dismissedUpdateVersion` semantically WAS a skip: pre-cadence,
  // "Later" wrote it and it silenced that version permanently. Adopt it, then
  // keep mirroring it back so a downgrade does not lose the decision.
  const skippedUpdateVersion =
    trimmedOrNull(raw.skippedUpdateVersion) ?? trimmedOrNull(raw.dismissedUpdateVersion);
  const dismissedUpdateVersion = skippedUpdateVersion;
  const updatePromptLastShownDay = trimmedOrNull(raw.updatePromptLastShownDay);
  const workspaceTransition = raw.workspaceTransition === false ? false : true;

  const timeFormat = raw.timeFormat === '12h' ? '12h' : '24h';
  const restoreLastSession = raw.restoreLastSession === false ? false : true;
  const validFreq = ['launch', 'daily', 'never'];
  const checkUpdatesFrequency = validFreq.includes(raw.checkUpdatesFrequency)
    ? raw.checkUpdatesFrequency : 'launch';
  const { sendUsageData, usageDataAsked } = normalizeUsageConsent(raw);
  const linkPreviews = raw.linkPreviews === true;
  const readReceipts = raw.readReceipts === false ? false : true;
  const desktopNotifications = raw.desktopNotifications === false ? false : true;
  const notificationSound = raw.notificationSound === false ? false : true;
  const closeToTray = raw.closeToTray === false ? false : true;
  const launchAtLoginApplied = raw.launchAtLoginApplied === true;

  const validDensity = ['compact', 'balanced', 'spacious'];
  const density = validDensity.includes(raw.density) ? raw.density : 'balanced';
  const sidebarPosition = raw.sidebarPosition === 'right' ? 'right' : 'left';
  const uiFontSize = Number.isFinite(raw.uiFontSize)
    ? Math.min(20, Math.max(10, raw.uiFontSize)) : 12;
  const showNavTooltips = raw.showNavTooltips === false ? false : true;
  const validAccentFg = ['auto', 'light', 'dark'];
  const accentFg = validAccentFg.includes(raw.accentFg) ? raw.accentFg : 'auto';

  const validFonts = ['JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Menlo', 'Consolas', 'monospace'];
  const editorFontFamily = validFonts.includes(raw.editorFontFamily)
    ? raw.editorFontFamily : 'JetBrains Mono';
  const editorFontSize = Number.isFinite(raw.editorFontSize)
    ? Math.min(24, Math.max(10, raw.editorFontSize)) : 12;
  const validTabSizes = [2, 4, 8];
  const editorTabSize = validTabSizes.includes(raw.editorTabSize) ? raw.editorTabSize : 4;
  const wordWrap = raw.wordWrap === false ? false : true;
  const stickyScroll = raw.stickyScroll === false ? false : true;
  const validViews = ['pretty', 'raw', 'preview'];
  const defaultResponseView = validViews.includes(raw.defaultResponseView)
    ? raw.defaultResponseView : 'pretty';
  const responseSizeLimitMb = Number.isFinite(raw.responseSizeLimitMb)
    ? Math.min(500, Math.max(0, Math.floor(raw.responseSizeLimitMb))) : 0;

  const requestTimeout = Number.isFinite(raw.requestTimeout)
    ? Math.max(0, raw.requestTimeout) : 30000;
  const followRedirects = raw.followRedirects === false ? false : true;
  const validHttpVersions = ['auto', '1.1', '2'];
  const httpVersion = validHttpVersions.includes(raw.httpVersion) ? raw.httpVersion : 'auto';
  const useSystemProxy = raw.useSystemProxy === true ? true : false;
  const proxyUrl = typeof raw.proxyUrl === 'string' ? raw.proxyUrl : '';

  // NOTE — this function is a WHITELIST: `saveAppPreferences` writes what it
  // returns, and `loadAppPreferences` reads through it too, so a key added to
  // DEFAULT_PREFERENCES but not returned below is silently discarded on every
  // save and resets to its default on every launch. `terminalFontSize` was in
  // exactly that state — the setting appeared to work (the store held it, the
  // terminals honoured it live) and was gone after a restart.
  const terminalFontSize = clampTerminalFontSize(
    raw.terminalFontSize ?? DEFAULT_PREFERENCES.terminalFontSize
  );
  const terminalCloseOnExit = normalizeCloseOnExit(raw.terminalCloseOnExit);
  const terminalTheme = normalizeTerminalTheme(raw.terminalTheme);
  const terminalOptionAsMeta = raw.terminalOptionAsMeta === true;
  const terminalSendCtrlK = raw.terminalSendCtrlK === true;
  const terminalNotifyLongCommands = raw.terminalNotifyLongCommands === false ? false : true;
  const terminalNotifySeconds = clampNotifySeconds(raw.terminalNotifySeconds ?? DEFAULT_PREFERENCES.terminalNotifySeconds);

  // Defaults ON, so the test is `=== false`: a stored `false` must survive, but
  // an absent key (an install predating this setting) gets the new default.
  const ftpShowHiddenFiles = raw.ftpShowHiddenFiles === false ? false : true;

  const mailCatcherPort = clampMailCatcherPort(raw.mailCatcherPort ?? DEFAULT_PREFERENCES.mailCatcherPort);
  // Defaults OFF, so the test is `=== true`.
  const mailCatcherAutoStart = raw.mailCatcherAutoStart === true;
  const mailCatcherKeep = clampMailCatcherKeep(raw.mailCatcherKeep ?? DEFAULT_PREFERENCES.mailCatcherKeep);
  const dbIdleDisconnect = normalizeDbIdleDisconnect(raw.dbIdleDisconnect);
  const dbTypeColors = normalizeDbTypeColors(raw.dbTypeColors);

  const chatLongMessageAsFile = raw.chatLongMessageAsFile === false ? false : true;
  const chatLongMessageKb = clampLongMessageKb(
    raw.chatLongMessageKb ?? DEFAULT_PREFERENCES.chatLongMessageKb
  );
  const chatShowSizeIndicator = raw.chatShowSizeIndicator === false ? false : true;
  // Defaults OFF, so the test is `=== true`: only an explicit opt-in survives.
  const aiReadResponses = raw.aiReadResponses === true;

  const keyboardShortcuts = normalizeKeyboardShortcuts(raw.keyboardShortcuts);

  return {
    version: 1,
    theme,
    language,
    accentColor,
    dismissedUpdateVersion,
    skippedUpdateVersion,
    updatePromptLastShownDay,
    workspaceTransition,
    timeFormat,
    restoreLastSession,
    checkUpdatesFrequency,
    sendUsageData,
    usageDataAsked,
    linkPreviews,
    readReceipts,
    desktopNotifications,
    notificationSound,
    closeToTray,
    launchAtLoginApplied,
    density,
    sidebarPosition,
    uiFontSize,
    showNavTooltips,
    accentFg,
    terminalFontSize,
    terminalCloseOnExit,
    terminalTheme,
    terminalOptionAsMeta,
    terminalSendCtrlK,
    terminalNotifyLongCommands,
    terminalNotifySeconds,
    ftpShowHiddenFiles,
    mailCatcherPort,
    mailCatcherAutoStart,
    mailCatcherKeep,
    dbIdleDisconnect,
    dbTypeColors,
    chatLongMessageAsFile,
    chatLongMessageKb,
    chatShowSizeIndicator,
    aiReadResponses,
    editorFontFamily,
    editorFontSize,
    editorTabSize,
    wordWrap,
    stickyScroll,
    defaultResponseView,
    responseSizeLimitMb,
    requestTimeout,
    followRedirects,
    httpVersion,
    useSystemProxy,
    proxyUrl,
    keyboardShortcuts,
  };
}

/**
 * One-time migration from old zustand `api-client-theme` key.
 * Must not override `apiboo-app-preferences` on every load (that forced theme back to light).
 */
function mergeWithLegacy(prefs, { fromNewStore = false } = {}) {
  if (fromNewStore) {
    clearLegacyThemeStorage();
    return prefs;
  }

  const legacyTheme = readLegacyTheme();
  if (!legacyTheme) return prefs;

  const migrated = normalizePreferences({ ...prefs, theme: legacyTheme });
  clearLegacyThemeStorage();
  return migrated;
}

/**
 * @returns {Promise<typeof DEFAULT_PREFERENCES>}
 */
export async function loadAppPreferences() {
  let fromDisk = null;
  // "There is no file yet" and "the file is there but unreadable" are NOT the
  // same thing, and conflating them loses settings. The first is a first run and
  // wants the defaults written out; the second is a truncated file, a locked
  // roaming profile or a transient FS error — writing defaults over it turns a
  // temporary read failure into permanent data loss. It matters most for the
  // opt-outs: a user who switched usage counting OFF would silently have it
  // switched back ON and persisted, with nothing to tell them.
  let readFailed = false;

  if (await prefersFilesystemStorage()) {
    try {
      const fs = await fsModule();
      const exists = await fs.exists(PREFS_FILE, {
        baseDir: fs.BaseDirectory.AppLocalData,
      });
      if (exists) {
        const text = await fs.readTextFile(PREFS_FILE, {
          baseDir: fs.BaseDirectory.AppLocalData,
        });
        fromDisk = JSON.parse(text);
      }
    } catch (err) {
      readFailed = true;
      console.warn('[appPreferencesStore] Failed to read AppData preferences:', err);
    }
  }

  const fromBrowser = readBrowserPrefs();
  const hadNewStore = Boolean(fromDisk ?? fromBrowser);
  const merged = normalizePreferences(fromDisk ?? fromBrowser ?? {});
  const withLegacy = mergeWithLegacy(merged, { fromNewStore: hadNewStore });

  // Only migrate/seed when the file genuinely is not there. After a failed read
  // the next successful save still writes, so nothing is stuck — but a bad read
  // never gets to overwrite settings it could not see.
  if (!fromDisk && !readFailed && (await prefersFilesystemStorage())) {
    try {
      await saveAppPreferences(withLegacy, { immediate: true });
    } catch {
      // ignore migration write failures
    }
  }

  // Same reasoning one level down: if the disk read FAILED and there was no
  // browser mirror either, `withLegacy` is nothing but defaults — writing it to
  // the mirror would make those defaults the "truth" the next launch reads back,
  // finishing the job of erasing whatever the unreadable file said.
  if (!(readFailed && !fromBrowser)) {
    try {
      localStorage.setItem(BROWSER_KEY, JSON.stringify(withLegacy));
    } catch {
      // ignore
    }
  }

  return withLegacy;
}

/**
 * @param {typeof DEFAULT_PREFERENCES} prefs
 * @param {{ immediate?: boolean }} [options]
 */
export async function saveAppPreferences(prefs, options = {}) {
  const normalized = normalizePreferences(prefs);
  const payload = JSON.stringify(normalized);

  const write = async () => {
    try {
      localStorage.setItem(BROWSER_KEY, payload);
      clearLegacyThemeStorage();
    } catch {
      // ignore
    }

    if (await prefersFilesystemStorage()) {
      const fs = await fsModule();
      await ensurePrefsDirFs();
      await fs.writeTextFile(PREFS_FILE, payload, {
        baseDir: fs.BaseDirectory.AppLocalData,
      });
    }
  };

  if (options.immediate) {
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    await write();
    return;
  }

  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    write().catch((err) => {
      console.warn('[appPreferencesStore] Failed to save preferences:', err);
    });
  }, 300);
}

/** @internal For unit tests */
export function mergeLegacyThemeForTests(prefs, options) {
  return mergeWithLegacy(prefs, options);
}

/** @internal For unit tests */
export function clearLegacyThemeStorageForTests() {
  clearLegacyThemeStorage();
}
